'use client';

import { useEyeTracker } from '@/hooks/useEyeTracker';
import { motion } from 'framer-motion';

interface EyeTrackerProps {
  className?: string;
}

export const EyeTracker = ({ className = '' }: EyeTrackerProps) => {
  const { eyePositions, isBlinking, manualBlink, containerRef } = useEyeTracker();

  return (
    <div
      ref={containerRef}
      className={`relative cursor-pointer select-none ${className}`}
      onClick={manualBlink}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          manualBlink();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label="Interactive animated eyes - click or press Enter to trigger blink"
    >
      <motion.div
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
      >
        <svg
          width="144"
          height="72"
          viewBox="0 0 144 72"
          className="dynamic-eyes"
          role="img"
          aria-label="Interactive eyes that follow your cursor"
          style={{
            filter: 'drop-shadow(0 4px 8px rgba(14, 165, 233, 0.3))',
            transition: 'transform 0.3s ease'
          }}
        >
          {/* Left Eye - Oval like real emoji */}
          <g>
            {/* Eye Socket */}
            <ellipse
              cx="36"
              cy="36"
              rx="24"
              ry="31"
              fill="#ffffff"
              stroke="#2d3748"
              strokeWidth="3"
            />

            {/* Pupil */}
            <motion.ellipse
              cx={36 + (eyePositions[0]?.pupilX || 0)}
              cy={36 + (eyePositions[0]?.pupilY || 0)}
              rx="12"
              ry="15.6"
              fill="#1a202c"
              animate={{
                scaleY: isBlinking ? 0.1 : 1,
              }}
              transition={{ duration: 0.1 }}
            />

            {/* Main Highlight */}
            <motion.circle
              cx={36 + (eyePositions[0]?.pupilX || 0) + 2.4}
              cy={36 + (eyePositions[0]?.pupilY || 0) - 6}
              r="4.8"
              fill="#ffffff"
              animate={{
                scaleY: isBlinking ? 0.1 : 1,
              }}
              transition={{ duration: 0.1 }}
            />

            {/* Mini Highlight */}
            <motion.circle
              cx={36 + (eyePositions[0]?.pupilX || 0) + 4.8}
              cy={36 + (eyePositions[0]?.pupilY || 0) + 2.4}
              r="1.8"
              fill="#ffffff"
              opacity="0.8"
              animate={{
                scaleY: isBlinking ? 0.1 : 1,
              }}
              transition={{ duration: 0.1 }}
            />

            {/* Eyelid for blinking */}
            <motion.ellipse
              cx="36"
              cy="36"
              rx="24"
              ry="31"
              fill="var(--background)"
              stroke="#2d3748"
              strokeWidth="3"
              initial={{ scaleY: 0 }}
              animate={{
                scaleY: isBlinking ? 1 : 0,
              }}
              style={{ transformOrigin: 'center bottom' }}
              transition={{ duration: 0.1 }}
            />
          </g>

          {/* Right Eye - Oval like real emoji */}
          <g>
            {/* Eye Socket */}
            <ellipse
              cx="108"
              cy="36"
              rx="24"
              ry="31"
              fill="#ffffff"
              stroke="#2d3748"
              strokeWidth="3"
            />

            {/* Pupil */}
            <motion.ellipse
              cx={108 + (eyePositions[1]?.pupilX || 0)}
              cy={36 + (eyePositions[1]?.pupilY || 0)}
              rx="12"
              ry="15.6"
              fill="#1a202c"
              animate={{
                scaleY: isBlinking ? 0.1 : 1,
              }}
              transition={{ duration: 0.1 }}
            />

            {/* Main Highlight */}
            <motion.circle
              cx={108 + (eyePositions[1]?.pupilX || 0) + 2.4}
              cy={36 + (eyePositions[1]?.pupilY || 0) - 6}
              r="4.8"
              fill="#ffffff"
              animate={{
                scaleY: isBlinking ? 0.1 : 1,
              }}
              transition={{ duration: 0.1 }}
            />

            {/* Mini Highlight */}
            <motion.circle
              cx={108 + (eyePositions[1]?.pupilX || 0) + 4.8}
              cy={36 + (eyePositions[1]?.pupilY || 0) + 2.4}
              r="1.8"
              fill="#ffffff"
              opacity="0.8"
              animate={{
                scaleY: isBlinking ? 0.1 : 1,
              }}
              transition={{ duration: 0.1 }}
            />

            {/* Eyelid for blinking */}
            <motion.ellipse
              cx="108"
              cy="36"
              rx="24"
              ry="31"
              fill="var(--background)"
              stroke="#2d3748"
              strokeWidth="3"
              initial={{ scaleY: 0 }}
              animate={{
                scaleY: isBlinking ? 1 : 0,
              }}
              style={{ transformOrigin: 'center bottom' }}
              transition={{ duration: 0.1 }}
            />
          </g>
        </svg>
      </motion.div>
    </div>
  );
};
